/* IncidentNotesModal.js — Bootstrap 5, clean overlay (no BS JS), polling, notes-only */
/* Drop-in: keeps your actions, polling, and Messages API the same */

import React, { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useAuth0 } from "@auth0/auth0-react";
import { hideModal } from "../../actions/modal";
import {
  fetchMessages,
  postNoteMessage,
  updatePendingMessage,
  clearPendingMessage,
  clearMessages,
} from "../../actions/messages";
import Messages from "../../views/Messages/Messages";
import "./IncidentNotesModal.css";

const IncidentNotesModal = () => {
  const dispatch = useDispatch();
  const { getAccessTokenSilently } = useAuth0();

  const timeoutRef = useRef(null);

  const visible = useSelector((state) => state.modal?.visible || false);
  const modalProps = useSelector((state) => state.modal?.modalProps || {});
  const messages = useSelector((state) => state.messages || {});
  const profile = useSelector((state) => state.profile);

  const { incident, agencyId, reportSchemaId } = modalProps;

  const hasProfile =
    profile && typeof profile.sub === "string" && profile.sub !== "";

  const hide = () => {
    // unlock body, remove our manual backdrop, then hide modal
    document.body.classList.remove("modal-open");
    document.body.style.overflow = "";
    document.body.style.paddingRight = "";
    const backdrop = document.querySelector(".notes-modal-backdrop");
    if (backdrop) backdrop.remove();
    dispatch(hideModal());
  };

  const closeNote = () => {
    hide();
  };

  const prepareFetch = async () => {
    if (!incident?._id || !agencyId || !reportSchemaId) {
      console.warn("prepareFetch: Missing identifiers:", {
        incident,
        agencyId,
        reportSchemaId,
      });
      return;
    }
    try {
      const token = await getAccessTokenSilently();
      dispatch(fetchMessages(incident._id, agencyId, reportSchemaId, token));
    } catch (error) {
      console.error("Error fetching token for messages:", error);
    }
  };

  const addMessage = async () => {
    // Only send when we have content, profile, and incident id
    const pending = messages.pendingMessage;
    if (!pending?.trim() || !hasProfile || !incident?._id) return;

    const message = {
      type: "note",
      display_name: profile.user_metadata?.name || profile.name,
      message: pending,
      sender_id: profile.sub, // keep consistent with Chat/Message styling
      sent: Date.now(),
    };

    try {
      const token = await getAccessTokenSilently();
      dispatch(
        postNoteMessage(incident._id, agencyId, reportSchemaId, message, token)
      );
      dispatch(clearPendingMessage());
    } catch (error) {
      console.error("Error sending note message:", error);
    }
  };

  // Fetch immediately when opening
  useEffect(() => {
    if (visible && incident?._id) {
      prepareFetch();
    }
    
  }, [visible, incident?._id]);

  // Poll every 10s while visible
  useEffect(() => {
    if (!visible || !incident?._id) return;
    clearTimeout(timeoutRef.current);
    timeoutRef.current = setInterval(() => {
      prepareFetch();
    }, 10000);
    return () => clearInterval(timeoutRef.current);
    
  }, [visible, incident?._id]);

  // Keep body scroll lock in sync with modal visibility
  useEffect(() => {
    if (visible) {
      document.body.classList.add("modal-open");
    } else {
      document.body.classList.remove("modal-open");
    }
    return () => document.body.classList.remove("modal-open");
  }, [visible]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // If you want to clear notes when the modal closes, uncomment the next line:
      // dispatch(clearMessages());
      hide();
      clearTimeout(timeoutRef.current);
    };
  
  }, []);

  if (!visible || !incident?._id) return null;

  // Optional debug (kept, but harmless)
  if (Array.isArray(messages?.messages)) {
    const types = messages.messages.map((m) => m.type);
    console.log("Message types:", types);
  }

  return (
    <div>
      {/* Manual backdrop */}
      <div className="notes-modal-backdrop fade show" />

      {/* Modal container — visibility via CSS classes (no Bootstrap JS) */}
      <div
        className={`notes-modal ${visible ? "show d-block" : ""}`}
        id="notes-modal"
        tabIndex="-1"
        role="dialog"
        aria-labelledby="notesModalLabel"
        aria-hidden={!visible}
        data-bs-backdrop="static"
        data-bs-keyboard="false"
      >
        <div className="notes-modal-dialog modal-dialog-centered" role="document">
          <div className="notes-modal-content">
            <div className="notes-modal-header">
              <h5
                className="notes-modal-title d-flex align-items-center"
                id="notesModalLabel"
              >
                <i className="material-icons me-2">note_add</i>
                <span>
                  Notes - {incident.display_title || "Incident # Unknown"} -{" "}
                  {incident.title || "Untitled"}
                </span>
              </h5>
              <button
                type="button"
                className="notes-modal-close"
                onClick={hide}
                aria-label="Close"
              >
                Close X
              </button>
            </div>

            <div className="notes-modal-body" id="notes-modal-body">
              <Messages
                closeTitle="Close Notes"
                incident={incident}
                // show ONLY notes in the Notes modal
                messages={
                  Array.isArray(messages.messages)
                    ? messages.messages.filter((m) => m.type === "note")
                    : []
                }
                pendingMessage={messages.pendingMessage || ""}
                hide={hide}
                clearMessages={clearMessages}
                updatePendingMessage={updatePendingMessage}
                addMessage={addMessage}
                closeModal={closeNote}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IncidentNotesModal;
